import {AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {Task, TaskControllerService} from "../../api";
import {filter} from "rxjs/operators";
import {animate, style, transition, trigger} from "@angular/animations";
import {ToolbarService} from "../util/design/toolbar/toolbar.service";
import {TaskListComponent} from "./task-list/task-list.component";
import {Subscription} from 'rxjs';
import {StateConfigService} from "../../services/state/state-config.service";
import {TaskDetailsService} from "../../services/tasks/task-details.service";
import {NotificationsService} from "../../services/notifications/notifications.service";
import {TaskBoardComponent} from "./task-board/task-board.component";
import {
  faArrowDownToBracket,
  faArrowDownWideShort,
  faArrowUpWideShort,
  faFilter,
  faSearch
} from "@fortawesome/pro-solid-svg-icons";
import {initFlowbite} from 'flowbite';
import {faIfc} from "../util/icons/custom-icons";
import {BicFilterComponent} from "../util/design/bic-filter/bic-filter.component";
import {TaskStatePipe} from "../../pipes/task-state.pipe";
import {PriorityPipe} from "../../pipes/priority.pipe";
import {PageHeaderComponent} from "../util/design/page-header/page-header.component";
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownTriggerDirective,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {TASK_SORT_FIELD_OPTIONS, TaskSortDirection, TaskSortField} from "./task-sort";
import {PermissionService} from "../../services/permission/permission.service";
import StateEnum = Task.StateEnum;
import PriorityEnum = Task.PriorityEnum;


/*interface navLink {
  label: string,
  link: string,
  index: number
}*/

enum ViewType {
  LIST = 0,
  BOARD = 1
}

@Component({
  selector: 'app-tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({opacity: 0, transform: 'translateX(50%)'}),
        animate('500ms ease-in', style({opacity: 1, transform: 'translateX(0%)'}))
      ]),
      transition(':leave', [
        style({opacity: 1, transform: 'translateX(0%)'}),
        animate('500ms ease-in', style({opacity: 0, transform: 'translateX(50%)'}))
      ])
    ]),
  ],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [

    TaskListComponent,
    TaskBoardComponent,
    BicFilterComponent,
    PageHeaderComponent,
    BicButtonComponent,
    BicDropdownComponent,
    BicDropdownTriggerDirective,
    TooltipDirective,
    FaIconComponent,
  ]
})
export class TasksComponent implements OnInit, AfterViewInit, OnDestroy {
  activatedRoute = inject(ActivatedRoute);
  taskControllerService = inject(TaskControllerService);
  stateConfigService = inject(StateConfigService);
  taskDetailsService = inject(TaskDetailsService);
  private router = inject(Router);
  private toolbar = inject(ToolbarService);
  private notificationsService = inject(NotificationsService);

  permissionService = inject(PermissionService);
  subscription: Subscription = new Subscription();
  viewType: typeof ViewType = ViewType;
  //activeLinkIndex = ViewType.LIST;
  activeLinkIndex = ViewType.BOARD;

  selectedTaskId!: string | undefined
  /** Zuletzt per Route geöffnete Aufgabe – ngOnInit und das NavigationEnd derselben Navigation feuern beide. */
  private routeTaskId: string | undefined;

  selectionActivated: boolean = false;

  searchAndFilter: {
    "search": string,
    "state": StateEnum[] | undefined,
    "priority": PriorityEnum[] | undefined,
    "dueDate": any[] | undefined,
    "assignedTo": any[]
  } = {search: "", state: undefined, priority: undefined, dueDate: undefined, assignedTo: []};


  @ViewChild('applist') applist: TaskListComponent | undefined;
  @ViewChild('board') board: TaskBoardComponent | undefined;
  @ViewChild('taskFilter') taskFilter: BicFilterComponent | undefined;

  assignedUsers: any[] = [];

  /**
   * Aktive Sortierung des Boards. Ohne sie wanderte eine Aufgabe beim Zuweisen einer Person an das
   * Spaltenende, weil sie im Board neu einsortiert wird (siehe taskChanged() im Board).
   */
  sortField: TaskSortField = 'name';
  sortDirection: TaskSortDirection = 'asc';
  protected readonly sortFieldOptions = TASK_SORT_FIELD_OPTIONS;

  /** Setzt Sortierfeld und -richtung direkt (je ein Pfeil-Button pro Feld im Dropdown). */
  applySort(field: TaskSortField, direction: TaskSortDirection): void {
    this.sortField = field;
    this.sortDirection = direction;
  }

  filterConfig = [
    {
      key: 'state',
      type: 'text',
      displayName: 'Status',
      getOptions: () => Object.values(StateEnum),
      pipe: new TaskStatePipe()
    },
    {
      key: 'priority',
      type: 'text',
      displayName: 'Priorität',
      getOptions: () => Object.values(PriorityEnum),
      pipe: new PriorityPipe()
    },
    {
      key: 'dueDate',
      type: 'date',
      displayName: 'Fälligkeitsdatum',
      options: ['Heute', 'Gestern', 'Letzte 7 Tage', 'Letzte 30 Tage', 'Letztes Jahr']
    },
    {
      key: 'assignedTo',
      type: 'text',
      displayName: 'Zugewiesen',
      getOptions: () => this.assignedUsers
    },
  ];

  ngAfterViewInit(): void {
        initFlowbite();
    }

  ngOnInit(): void {
    const projectId = this.stateConfigService.getProjectId();
    if (projectId) {
      this.taskDetailsService.loadProjectUsers(projectId).subscribe();
    }

    this.toolbar.config = {
      add: {
        disabled: () => !this.permissionService.hasProjectPrivilege('PROJECT_TASK_EDIT'),
        action: () => this.selectedTaskId = undefined
      }
    }
    this.selectedTaskId = this.activatedRoute.snapshot.firstChild?.params.id ?? undefined
    this.openTaskFromRoute(this.selectedTaskId);
    //this.activeLinkIndex = this.activatedRoute.snapshot.firstChild?.data.tab ?? 0

    this.subscription = this.router.events
      .pipe(
        filter(value => value instanceof NavigationEnd),
      )
      .subscribe(event => {

        this.selectedTaskId = this.activatedRoute.snapshot.firstChild?.params.id ?? undefined
        this.openTaskFromRoute(this.selectedTaskId);
        if (this.selectedTaskId == undefined) {
          this.applist?.refreshList();
        }
        // Ohne `tab` in der Route die aktuelle Ansicht behalten: `?? 0` schaltete bei jeder Navigation
        // innerhalb der Seite (z. B. Deep-Link tasks/:id) auf die alte Tabellenansicht LIST um.
        this.activeLinkIndex = this.activatedRoute.snapshot.firstChild?.data.tab ?? this.activeLinkIndex
      });
  }

  safeDelete(obj: any, key: string): void {
        const keyPath = key.split(".");
        let currentObj = obj;
        for (let index = 0; index < keyPath.length ; index++) {
          if (currentObj && keyPath[index] in currentObj) {
            if (keyPath.indexOf(keyPath[index]) === keyPath.length - 1) {
              delete currentObj[keyPath[index]];
            } else {
              currentObj = currentObj[keyPath[index]];
            }
          } else if (Array.isArray(currentObj)) {
            currentObj.forEach(obj => {
              if (obj && keyPath[index] in obj) {
                if (keyPath.indexOf(keyPath[index]) === keyPath.length - 1) {
                  delete obj[keyPath[index]];
                } else {
                  this.safeDelete(obj, keyPath.slice(index).join('.'))
                }
              } else {
                this.safeDelete(obj, keyPath.slice(index).join('.'))
              }
            })
          } else {
            break;
          }
        }
  }

  removeViewpointFromComment(task:any){
    task["markup"]["comment"].forEach((comment:any)=>{
      if(comment["viewpoint"]) {
        delete comment["viewpoint"]["viewpoint"]
      }
    })
  }

  addBICInformation(task:Task){
    if(task.lph){
      task.description?.concat(" LPH: " + task.lph)
    }
    if (task.priority){
      task.description?.concat(" Priority: " + task.priority)
    }
    if (task.state){
      task.description?.concat(" State: " + task.state)
    }
    if (task.projectId) {
      task.description?.concat(" Project: " + task.projectId)
    }
  }

  changeViewType(viewType:number) {
    this.activeLinkIndex = viewType;
  }

  onFilterChange(filters: { [key: string]: any[] }) {
    this.searchAndFilter = {
      search: this.searchAndFilter.search,
      state: filters['state']?.length ? filters['state'] : undefined,
      priority: filters['priority']?.length ? filters['priority'] : undefined,
      dueDate: filters['dueDate']?.length ? filters['dueDate'] : undefined,
      assignedTo: filters['assignedTo']?.length ? filters['assignedTo'] : []
    };
  }

  /**
   * Deep-Link `projects/:p/tasks/:taskId` (z. B. aus chatBIC oder einer Mail): öffnet das Aufgaben-Popup.
   * Die Route gab es schon, ausgewertet wurde sie nie – das Popup öffnet sonst nur der Klick in der Liste.
   * Danach zurück auf die Listen-URL, damit Reload und Zurück das Popup nicht erneut öffnen.
   */
  private openTaskFromRoute(taskId: string | undefined): void {
    if (!taskId || taskId === 'create') {
      this.routeTaskId = undefined;
      return;
    }
    if (taskId === this.routeTaskId) return;
    this.routeTaskId = taskId;
    this.taskControllerService.getTask(taskId).subscribe({
      next: task => {
        this.taskDetailsService.setOpenedTask(task);
        this.router.navigate(['..'], {relativeTo: this.activatedRoute.firstChild ?? this.activatedRoute, replaceUrl: true});
      },
      error: () => {
        this.notificationsService.showErrorMessage('Die Aufgabe wurde nicht gefunden oder Sie haben keinen Zugriff darauf.');
        this.router.navigate(['..'], {relativeTo: this.activatedRoute.firstChild ?? this.activatedRoute, replaceUrl: true});
      },
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }
  protected readonly faSearch = faSearch;
  protected readonly faFilter = faFilter;
  protected readonly faIfc = faIfc;
  protected readonly faArrowDownToBracket = faArrowDownToBracket;
  protected readonly faArrowDownWideShort = faArrowDownWideShort;
  protected readonly faArrowUpWideShort = faArrowUpWideShort;

  search(event: any) {
    this.searchAndFilter = {...this.searchAndFilter, search: event.target.value};
  }

  resetSearch() {
    this.searchAndFilter = {
      search: '',
      state: undefined,
      priority: undefined,
      dueDate: undefined,
      assignedTo: []
    };
    this.taskFilter?.clearAll();
  }

  onAssignedUsersChanged(users: any[]) {
    this.assignedUsers = users;
  }
}
